import { format, startOfWeek, endOfWeek, addWeeks, parseISO, isWithinInterval } from 'date-fns';

/**
 * WeeklyCapacityService
 * Manages weekly task capacity and prevents overwhelming weeks
 * Implements sophisticated task distribution based on student preferences
 */
class WeeklyCapacityService {
  
  // Default capacity hours per week by timeline pressure
  static DEFAULT_CAPACITIES = {
    'crisis': 20,        // High intensity, short duration
    'intensive': 15,     // High productivity periods  
    'steady': 12,        // Balanced sustainable pace
    'comprehensive': 10  // Thorough, methodical approach
  };

  // Task complexity weight multipliers
  static TASK_COMPLEXITY_WEIGHTS = {
    'crisis': 0.5,        // Crisis tasks are streamlined
    'standard': 1.0,      // Standard complexity
    'comprehensive': 1.5  // More thorough, takes longer
  };

  // Capacity warning thresholds
  static CAPACITY_THRESHOLDS = {
    OPTIMAL: 0.8,      // 80% - optimal capacity utilization
    WARNING: 1.2,      // 120% - getting overloaded
    CRITICAL: 1.5      // 150% - definitely overloaded
  };

  /**
   * Calculate weekly capacity limits based on student preferences
   */
  static calculateWeeklyCapacity(preferences = {}) {
    const timelinePressure = preferences.disclosure_pace || 'steady';
    const workloadCapacity = preferences.workload_capacity || 'moderate';
    const availableHours = preferences.available_hours_per_week || 8;
    
    // Base capacity from timeline pressure
    const baseCapacity = this.DEFAULT_CAPACITIES[timelinePressure] || this.DEFAULT_CAPACITIES['steady'];
    
    // Workload capacity modifiers
    const workloadMultipliers = {
      'light': 0.7,
      'moderate': 1.0, 
      'intensive': 1.4
    };
    
    const workloadMultiplier = workloadMultipliers[workloadCapacity] || 1.0;
    
    // Final calculation: min of user hours and calculated capacity
    const calculatedCapacity = Math.round(baseCapacity * workloadMultiplier);
    const finalCapacity = Math.min(availableHours, calculatedCapacity);

    // Removed console.log to prevent spam during repeated calculations

    return {
      maxHoursPerWeek: finalCapacity,
      optimalHoursPerWeek: Math.round(finalCapacity * this.CAPACITY_THRESHOLDS.OPTIMAL),
      warningThreshold: Math.round(finalCapacity * this.CAPACITY_THRESHOLDS.WARNING),
      criticalThreshold: Math.round(finalCapacity * this.CAPACITY_THRESHOLDS.CRITICAL),
      timelinePressure,
      workloadCapacity
    };
  }

  /**
   * Calculate task effort in hours based on complexity and estimated duration
   */
  static calculateTaskEffort(task) {
    const baseDuration = task.estimated_duration || 60; // minutes
    const intensity = task.intensity_level || 'standard';
    const complexityWeight = this.TASK_COMPLEXITY_WEIGHTS[intensity] || 1.0;
    
    // Convert to hours and apply complexity weight
    const effortHours = (baseDuration / 60) * complexityWeight;
    
    return Math.round(effortHours * 10) / 10; // Round to 1 decimal
  }

  /**
   * Analyze current weekly distribution and identify overloaded weeks
   */
  static analyzeWeeklyDistribution(tasks, capacity) {
    const weeklyDistribution = new Map();
    
    // Group tasks by week
    tasks.forEach(task => {
      const taskDate = new Date(task.due_date || task.deadline || task.scheduled_date);
      const weekStart = startOfWeek(taskDate, { weekStartsOn: 1 }); // Monday start
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      
      if (!weeklyDistribution.has(weekKey)) {
        weeklyDistribution.set(weekKey, {
          weekStart,
          weekEnd: endOfWeek(taskDate, { weekStartsOn: 1 }),
          tasks: [],
          totalHours: 0,
          capacityUtilization: 0
        });
      }
      
      const week = weeklyDistribution.get(weekKey);
      const taskEffort = this.calculateTaskEffort(task);
      
      week.tasks.push({
        ...task,
        effortHours: taskEffort
      });
      week.totalHours += taskEffort;
      week.capacityUtilization = week.totalHours / capacity.maxHoursPerWeek;
    });

    // Convert to array and sort by date
    const weeks = Array.from(weeklyDistribution.values())
      .sort((a, b) => a.weekStart - b.weekStart);

    // Identify problematic weeks
    const analysis = {
      weeks,
      overloadedWeeks: weeks.filter(w => w.capacityUtilization > this.CAPACITY_THRESHOLDS.CRITICAL),
      warningWeeks: weeks.filter(w => 
        w.capacityUtilization > this.CAPACITY_THRESHOLDS.WARNING && 
        w.capacityUtilization <= this.CAPACITY_THRESHOLDS.CRITICAL
      ),
      optimalWeeks: weeks.filter(w => w.capacityUtilization <= this.CAPACITY_THRESHOLDS.OPTIMAL),
      totalTasks: tasks.length,
      totalHours: weeks.reduce((sum, w) => sum + w.totalHours, 0)
    };

    console.log(`[WeeklyCapacityService] Weekly distribution analysis:`, {
      totalWeeks: weeks.length,
      overloadedWeeks: analysis.overloadedWeeks.length,
      warningWeeks: analysis.warningWeeks.length,
      optimalWeeks: analysis.optimalWeeks.length,
      averageUtilization: Math.round((analysis.totalHours / (weeks.length * capacity.maxHoursPerWeek)) * 100)
    });

    return analysis;
  }

  /**
   * Redistribute tasks from overloaded weeks to available capacity
   */
  static redistributeOverloadedTasks(tasks, preferences = {}) {
    const capacity = this.calculateWeeklyCapacity(preferences);
    const analysis = this.analyzeWeeklyDistribution(tasks, capacity);
    
    if (analysis.overloadedWeeks.length === 0) {
      console.log(`[WeeklyCapacityService] No overloaded weeks found, distribution looks good`);
      return tasks;
    }
    
    console.log(`[WeeklyCapacityService] Redistributing ${analysis.overloadedWeeks.length} overloaded weeks`);
    
    const redistributedTasks = [...tasks];
    
    // Process each overloaded week
    for (const overloadedWeek of analysis.overloadedWeeks) {
      const excessHours = overloadedWeek.totalHours - capacity.maxHoursPerWeek;
      console.log(`[WeeklyCapacityService] Week ${format(overloadedWeek.weekStart, 'MMM dd')} is overloaded by ${excessHours}h`);
      
      // Sort tasks by priority and flexibility for redistribution
      const redistributableTasks = overloadedWeek.tasks
        .filter(task => this.isTaskRedistributable(task))
        .sort((a, b) => {
          // Lower priority tasks get moved first
          const priorityOrder = { 'low': 0, 'medium': 1, 'high': 2, 'critical': 3 };
          const priorityDiff = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
          if (priorityDiff !== 0) return priorityDiff;
          
          // Longer tasks get moved first (easier to find slots)
          return b.effortHours - a.effortHours;
        });
      
      let remainingExcess = excessHours;
      
      for (const task of redistributableTasks) {
        if (remainingExcess <= 0) break;
        
        // Find best week to move this task to
        const targetWeek = this.findBestWeekForTask(task, analysis.weeks, capacity);
        
        if (targetWeek && targetWeek !== overloadedWeek) {
          // Move task to target week
          const taskIndex = redistributedTasks.findIndex(t => t.id === task.id);
          if (taskIndex >= 0) {
            const newDueDate = this.calculateNewTaskDate(task, targetWeek.weekStart);
            redistributedTasks[taskIndex] = {
              ...redistributedTasks[taskIndex],
              due_date: format(newDueDate, 'yyyy-MM-dd'),
              redistribution_reason: `Moved from overloaded week (${Math.round(overloadedWeek.capacityUtilization * 100)}% capacity)`
            };
            
            remainingExcess -= task.effortHours;
            console.log(`[WeeklyCapacityService] Moved "${task.title}" to week ${format(targetWeek.weekStart, 'MMM dd')}`);
          }
        }
      }
    }
    
    // Re-analyze after redistribution
    const finalAnalysis = this.analyzeWeeklyDistribution(redistributedTasks, capacity);
    console.log(`[WeeklyCapacityService] Redistribution complete:`, {
      before: { overloadedWeeks: analysis.overloadedWeeks.length },
      after: { overloadedWeeks: finalAnalysis.overloadedWeeks.length },
      tasksRelocated: redistributedTasks.filter(t => t.redistribution_reason).length
    });
    
    return redistributedTasks;
  }

  /**
   * Check if a task can be redistributed to another week
   */
  static isTaskRedistributable(task) {
    // Don't move deadline-driven tasks or critical dependencies
    if (task.anchor_id && task.anchor_id !== 'rolling_admission') {
      return false;
    }
    
    // Don't move critical priority tasks unless absolutely necessary
    if (task.priority === 'critical') {
      return false;
    }
    
    // Don't move tasks with strict dependencies
    if (task.template_dependencies && task.template_dependencies.length > 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Find the best week to move a task to based on available capacity
   */
  static findBestWeekForTask(task, weeks, capacity) {
    const taskDate = new Date(task.due_date || task.deadline || task.scheduled_date);
    
    // Only consider weeks within reasonable range (4 weeks before or after)
    const minDate = new Date(taskDate);
    minDate.setDate(minDate.getDate() - 28);
    const maxDate = new Date(taskDate);
    maxDate.setDate(maxDate.getDate() + 28);
    
    const candidateWeeks = weeks.filter(week => 
      week.weekStart >= minDate && 
      week.weekStart <= maxDate &&
      week.totalHours + task.effortHours <= capacity.maxHoursPerWeek
    );
    
    if (candidateWeeks.length === 0) return null;
    
    // Prefer weeks closest to original date with most available capacity
    return candidateWeeks
      .map(week => ({
        ...week,
        availableCapacity: capacity.maxHoursPerWeek - week.totalHours,
        distanceFromOriginal: Math.abs(week.weekStart - taskDate) / (1000 * 60 * 60 * 24)
      }))
      .sort((a, b) => {
        // First sort by available capacity (descending)
        const capacityDiff = b.availableCapacity - a.availableCapacity;
        if (Math.abs(capacityDiff) > 2) return capacityDiff; // Significant capacity difference
        
        // Then by distance from original (ascending)
        return a.distanceFromOriginal - b.distanceFromOriginal;
      })[0];
  }

  /**
   * Calculate new task date within target week, respecting business days
   */
  static calculateNewTaskDate(task, weekStart) {
    const businessDaysOnly = task.business_days_only !== false;
    const avoidWeekends = task.avoid_weekends !== false;
    
    if (businessDaysOnly || avoidWeekends) {
      // Place on Wednesday (middle of week) for business-day tasks
      const newDate = new Date(weekStart);
      newDate.setDate(newDate.getDate() + 2); // Wednesday
      return newDate;
    } else {
      // Place on Sunday (end of week) for weekend-friendly tasks  
      const newDate = new Date(weekStart);
      newDate.setDate(newDate.getDate() + 6); // Sunday
      return newDate;
    }
  }

  /**
   * Generate capacity management report
   */
  static generateCapacityReport(tasks, preferences = {}) {
    const capacity = this.calculateWeeklyCapacity(preferences);
    const analysis = this.analyzeWeeklyDistribution(tasks, capacity);
    
    return {
      capacity,
      distribution: analysis,
      recommendations: this.generateCapacityRecommendations(analysis, capacity),
      summary: {
        totalTasks: analysis.totalTasks,
        totalHours: Math.round(analysis.totalHours * 10) / 10,
        averageWeeklyHours: Math.round((analysis.totalHours / analysis.weeks.length) * 10) / 10,
        peakWeekHours: Math.max(...analysis.weeks.map(w => w.totalHours)),
        capacityUtilization: Math.round((analysis.totalHours / (analysis.weeks.length * capacity.maxHoursPerWeek)) * 100)
      }
    };
  }

  /**
   * Generate recommendations for capacity optimization
   */
  static generateCapacityRecommendations(analysis, capacity) {
    const recommendations = [];
    
    if (analysis.overloadedWeeks.length > 0) {
      recommendations.push({
        type: 'overload_warning',
        priority: 'high',
        message: `${analysis.overloadedWeeks.length} week${analysis.overloadedWeeks.length > 1 ? 's' : ''} exceed capacity limits`,
        action: 'redistribute_tasks',
        weeks: analysis.overloadedWeeks.map(w => format(w.weekStart, 'MMM dd'))
      });
    }
    
    if (analysis.warningWeeks.length > 0) {
      recommendations.push({
        type: 'capacity_warning', 
        priority: 'medium',
        message: `${analysis.warningWeeks.length} week${analysis.warningWeeks.length > 1 ? 's' : ''} approaching capacity limits`,
        action: 'monitor_workload',
        weeks: analysis.warningWeeks.map(w => format(w.weekStart, 'MMM dd'))
      });
    }
    
    if (analysis.optimalWeeks.length > analysis.weeks.length * 0.7) {
      recommendations.push({
        type: 'capacity_opportunity',
        priority: 'low', 
        message: 'Good capacity distribution - consider adding optional tasks',
        action: 'add_comprehensive_tasks'
      });
    }
    
    return recommendations;
  }
}

export default WeeklyCapacityService;